<script setup>
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import { ref, computed, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { usePlanningStore } from '@/stores/planning.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore, searchResultTypeEnum } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ProjectListItem from '@/components/shared/ProjectListItem.vue'
import ProjectDeliveryNoteDialog from '@/components/planner/ProjectDeliveryNoteDialog.vue'
import TimelineDetail from '@/components/shared/TimelineDetail.vue'
import BudgetDocumentDetail from '@/components/accounting/BudgetDocumentDetail.vue'
import BudgetDocumentPrintDialog from '@/components/accounting/BudgetDocumentPrintDialog.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'
import CostReportDetail from '@/components/accounting/CostReportDetail.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import ProjectAttachmentsSection from '@/components/shared/ProjectAttachmentsSection.vue'
import ProjectTasksList from '@/components/task/ProjectTasksList.vue'

import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

const { isMobile } = useDetectDevice()
const router = useRouter()

const i18n = getI18n()
const planningStore = usePlanningStore()
const { getProjectTagChips } = useChips()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()

useNavigator(() => {
  if (!editItem.value) {
    emit('cancel')
  }
})

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  warnings: {
    type: Object,
    required: false,
  },
  showEditProjectButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  // Kept for backward compatibility with callers (e.g. ProjectListingItemsGrid); the
  // detail view is now always the tabbed layout, so this no longer changes behaviour.
  isEditMode: {
    type: Boolean,
    default: false,
  },
})
useChatStore().trackPageEntity(searchResultTypeEnum.PROJECT, () => props.modelValue?.id)

const emit = defineEmits(['cancel', 'update'])

const projectItem = ref(props.modelValue)
const editItem = ref(null)
const editInitialStep = ref(planningStore.projectEditStepEnum.BASIC_INFO)
const eventWarnings = ref(props.warnings || [])
const showChangeStatusDialog = ref(false)
const selectedStatus = ref(null)
const showDeliveryNoteDialog = ref(false)
const printBudgetDocumentId = ref(null)
const invoicedProjectIds = ref([])
const projectTasksListRef = ref(null)

const tabEnum = Object.freeze({ BUDGET: 'budget', COST_REPORT: 'costReport', ALLOCATIONS: 'allocations', TASKS: 'tasks' })

const goToWarehouseMovements = () => {
  router.push({ name: 'warehouse-list', query: { project: projectItem.value.id } })
}
const activeTab = ref(tabEnum.BUDGET)
const budgetMenuActions = ref([])

const budgetDocumentId = computed(() => projectItem.value.budget_document?.id ?? null)

const projectHeaderQuoteActionKeys = Object.freeze(['edit', 'magicLink'])
const headerMenuActions = computed(() => budgetMenuActions.value.filter(a => projectHeaderQuoteActionKeys.includes(a.key)))

const refreshProject = async () => {
  projectItem.value = (await planningStore.fetchProjectByIdAsync(projectItem.value.id)).data
}

const updateEventWarnings = async () => {
  const startOfCurrentDate = dayjs().startOf('day').toISOString()
  const endOfCurrentDate = dayjs().endOf('day').toISOString()
  eventWarnings.value = (await scheduleStore.fetchEventWarningsAsync({ 'start': startOfCurrentDate, 'end': endOfCurrentDate, 'project_id': projectItem.value.id })).data
}

const onAddTask = async () => {
  activeTab.value = tabEnum.TASKS
  await nextTick()
  projectTasksListRef.value?.addTask()
}

onMounted(async () => {
  if (!props.warnings) await updateEventWarnings()
})

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => {
    await planningStore.archiveProjectAsync(id)
  },
  onDelete: async(id, config) => { await planningStore.deleteProjectAsync(id, config) },
})

const onArchive = async(project) => {
  await archiveAsync(project.id, project.archived)
  project.archived = !project.archived
}

const onEditProject = () => {
  editInitialStep.value = planningStore.projectEditStepEnum.BASIC_INFO
  editItem.value = { ...projectItem.value }
}

const onAttachFile = () => {
  editInitialStep.value = planningStore.projectEditStepEnum.ATTACHMENTS
  editItem.value = { ...projectItem.value }
}

const onProjectUpdate = async () => {
  editItem.value = null
  await refreshProject()
}

const onProjectDelete = async () => {
  editItem.value = null
  onCancel()
}

const onAllocationsUpdate = async () => {
  await Promise.all([refreshProject(), updateEventWarnings()])
}

const projectStatuses = planningStore.getLocalizedProjectStatuses().map(s => ({ label: s.name, value: s.id }))

const onChangeStatusClick = () => {
  selectedStatus.value = projectItem.value.status
  showChangeStatusDialog.value = true
}

const onConfirmStatusChange = async () => {
  if (selectedStatus.value !== undefined && selectedStatus.value !== projectItem.value.status) {
    await planningStore.upsertProjectAsync({ id: projectItem.value.id, status: selectedStatus.value })
  }
  showChangeStatusDialog.value = false
  await refreshProject()
  emit('update')
}

const getFilteredProjectServices = (event) => {
  const project = {...planningStore.getProjectById(event.project_id)}
  project.services = project.services?.filter(s => s === event.service_id) || []
  return project
}

const onCancel = () => emit('cancel')

useEntityChanges([entityChangeTypeEnum.PROJECT], (change) => (!change.id || change.id === projectItem.value?.id) && refreshProject(), () => editItem.value !== null)
</script>
<template>
  <archive-warning-dialog
    v-model="archiveWarning"
    @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
  />
  <q-card class="dialog-card-flex">
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'budget-content-scroll': activeTab === tabEnum.BUDGET || activeTab === tabEnum.ALLOCATIONS }">
      <navigate-back
        :text="projectItem.name"
        :icon="props.showEditProjectButton ? (projectItem.status !== planningStore.projectStatusEnum.PLANNED ? 'mdi-magnify' : 'mdi-pencil') : null"
        :archived="projectItem.archived"
        @cancel="onCancel"
        @click="onEditProject"
      >
        <template #right>
          <div style="display: flex; align-items: center;">
            <q-btn
              flat
              round
              icon="mdi-qrcode-scan"
              @click="goToWarehouseMovements"
            >
              <q-tooltip>{{ i18n.t('common.warehouseMovements') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="!projectItem.archived"
              flat
              round
              icon="mdi-pencil"
              @click="onEditProject"
            />
            <q-btn flat round icon="mdi-dots-vertical">
              <q-menu anchor="bottom right" self="top right">
                <q-list style="min-width: 12.5rem">
                  <q-item
                    v-close-popup
                    clickable
                    :disable="!budgetDocumentId"
                    @click="printBudgetDocumentId = budgetDocumentId"
                  >
                    <q-item-section avatar><q-icon name="mdi-printer" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.printQuote') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="showDeliveryNoteDialog = true"
                  >
                    <q-item-section avatar><q-icon name="mdi-printer" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.printDeliveryNote') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.INVOICING_FULL) && !projectItem.archived"
                    v-close-popup
                    clickable
                    @click="invoicedProjectIds = [projectItem.id]"
                  >
                    <q-item-section avatar><q-icon name="mdi-file-document-plus-outline" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.generateInvoice') }}</q-item-section>
                  </q-item>
                  <template v-if="headerMenuActions.length">
                    <q-separator />
                    <q-item-label header class="q-py-sm">{{ i18n.t('common.budgetDocument') }}</q-item-label>
                    <q-item
                      v-for="action in headerMenuActions"
                      :key="action.key"
                      v-close-popup
                      clickable
                      @click="action.handler()"
                    >
                      <q-item-section avatar><q-icon :name="action.icon" /></q-item-section>
                      <q-item-section>{{ action.label }}</q-item-section>
                    </q-item>
                  </template>
                  <q-separator />
                  <q-item
                    v-if="!projectItem.archived"
                    v-close-popup
                    clickable
                    @click="onChangeStatusClick"
                  >
                    <q-item-section avatar><q-icon name="mdi-swap-horizontal" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.status') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-if="!projectItem.archived"
                    v-close-popup
                    clickable
                    @click="onAddTask"
                  >
                    <q-item-section avatar><q-icon name="mdi-checkbox-marked-circle-plus-outline" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.tasks.newTask') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-if="!projectItem.archived"
                    v-close-popup
                    clickable
                    @click="onAttachFile"
                  >
                    <q-item-section avatar><q-icon name="mdi-attachment" /></q-item-section>
                    <q-item-section>{{ i18n.t('common.attachFile') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="onArchive(projectItem)"
                  >
                    <q-item-section avatar><q-icon :name="projectItem?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'" /></q-item-section>
                    <q-item-section>{{ i18n.t(projectItem?.archived ? 'common.unarchive' : 'common.archive') }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </template>
      </navigate-back>
      <!-- Common project header: visible in all tabs -->
      <q-card-section class="q-pt-none q-pb-none q-px-sm project-common-header">
        <q-list>
          <q-item>
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>{{ i18n.t('common.period') }}</q-item-label>
                <q-item-label class="align-items-baseline">
                  <q-icon name="mdi-clock-outline" class="q-mr-xs" />
                  <span>{{ dayjs(projectItem.start).format('HH:mm') }} - {{ dayjs(projectItem.end).format('HH:mm') }}</span>
                </q-item-label>
                <q-item-label class="align-items-baseline">
                  <q-icon name="mdi-calendar" class="q-mr-xs" />
                  <span>{{ dayjs(projectItem.start).format('DD/MM') }} to {{ dayjs(projectItem.end).format('DD/MM') }}</span>
                </q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section style="display: flex; align-content: flex-end;">
                <q-item-label caption style="text-align: right;">{{ i18n.t('common.status') }}</q-item-label>
                <q-item-label>
                  <chip-template
                    :values="[planningStore.getLocalizedProjectStatuses().find(i => i.id === projectItem.status)]"
                    :style="{ 'margin-right': '0' }"
                  />
                </q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item v-if="projectItem.tag_ids?.length">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.tag', 2) }}</q-item-label>
              <q-item-label>
                <chip-template
                  :values="getProjectTagChips(projectItem.tag_ids)"
                />
              </q-item-label>
            </q-item-section>
          </q-item>
          <description-section v-if="projectItem.description" :text="projectItem.description" />
        </q-list>
      </q-card-section>
      <q-separator />
      <q-tab-panels v-model="activeTab" animated keep-alive>
        <!-- Quote: files, warnings, then budget rows -->
        <q-tab-panel :name="tabEnum.BUDGET" class="q-pa-none budget-tab-panel">
          <div class="budget-extras">
            <template v-if="projectItem.project_files?.length">
              <q-card-section class="q-pt-none q-pb-none q-px-sm">
                <q-list>
                  <project-attachments-section :files="projectItem.project_files" />
                </q-list>
              </q-card-section>
              <q-separator />
            </template>
            <template v-if="eventWarnings?.length">
              <q-card-section class="q-pt-none">
                <q-list>
                  <div v-for="warning in eventWarnings" :key="warning.id">
                    <q-item-label header class="q-pl-none">
                      {{ i18n.t(`warning.event.${planningStore.getEventWarningTypeName(warning.type)}`) }}
                      <q-icon name="mdi-alert" size="xs" color="warning" />
                    </q-item-label>
                    <q-list>
                      <project-list-item
                        v-for="event in warning.events"
                        :key="event.id"
                        class="q-ml-md"
                        show-staff
                        show-equipment
                        :start="event.start"
                        :end="event.end"
                        :item="getFilteredProjectServices(event)"
                      />
                    </q-list>
                  </div>
                </q-list>
              </q-card-section>
              <q-separator />
            </template>
          </div>
          <div class="budget-embedded">
            <Suspense v-if="budgetDocumentId">
              <budget-document-detail
                :key="budgetDocumentId"
                :item-id="budgetDocumentId"
                hide-header
                @menu-actions="budgetMenuActions = $event"
              />
            </Suspense>
            <div v-else class="text-grey text-caption q-pa-md">
              {{ i18n.t('common.noBudgetDocument') }}
            </div>
          </div>
        </q-tab-panel>
        <!-- Consuntivo: cost report keeps its own header -->
        <q-tab-panel :name="tabEnum.COST_REPORT" class="q-pa-none">
          <cost-report-detail
            :hide-header="true"
            :hide-project-info="true"
            :project="projectItem"
            @cancel="onCancel"
            @update="refreshProject"
          />
        </q-tab-panel>
        <!-- Timeline: TimelineDetail owns its own header -->
        <q-tab-panel :name="tabEnum.ALLOCATIONS" class="q-pa-none allocations-tab-panel">
          <timeline-detail
            :model-value="projectItem"
            :hide-header="true"
            :hide-project-info="true"
            :show-edit-project-button="props.showEditProjectButton"
            @cancel="onCancel"
            @update="onAllocationsUpdate"
            @edit="onEditProject"
            @change-status="onChangeStatusClick"
            @delivery-note="showDeliveryNoteDialog = true"
            @archive="onArchive(projectItem)"
          />
        </q-tab-panel>
        <q-tab-panel :name="tabEnum.TASKS" class="q-pa-sm tasks-tab-panel">
          <project-tasks-list ref="projectTasksListRef" :project-id="projectItem.id" />
        </q-tab-panel>
      </q-tab-panels>
    </q-card-section>
    <q-separator />
    <q-tabs
      v-model="activeTab"
      align="justify"
      active-color="primary"
      indicator-color="primary"
      class="text-grey"
    >
      <q-tab :name="tabEnum.BUDGET" :label="i18n.t('common.budgetDocument')" />
      <q-tab :name="tabEnum.COST_REPORT" :label="i18n.t('common.costReport')" />
      <q-tab :name="tabEnum.ALLOCATIONS" :label="i18n.t('common.timeline')" />
      <q-tab :name="tabEnum.TASKS" :label="i18n.t('common.tasks.tab')" />
    </q-tabs>
  </q-card>
  <q-dialog
    :model-value="editItem !== null"
    :maximized="isMobile"
    persistent
    class="dialog-sm"
  >
    <project-edit
      v-if="editItem !== null"
      :model-value="editItem"
      :initial-step="editInitialStep"
      @delete="onProjectDelete"
      @update="onProjectUpdate"
      @cancel="editItem = null"
    />
  </q-dialog>
  <q-dialog
    :model-value="printBudgetDocumentId !== null"
    :maximized="isMobile"
    persistent
    @hide="printBudgetDocumentId = null"
  >
    <budget-document-print-dialog
      v-if="printBudgetDocumentId !== null"
      :budget-document-id="printBudgetDocumentId"
      @cancel="printBudgetDocumentId = null"
    />
  </q-dialog>
  <q-dialog
    :model-value="invoicedProjectIds.length > 0"
    :maximized="isMobile"
    persistent
    class="dialog-xl"
  >
    <invoice-edit
      v-if="invoicedProjectIds.length > 0"
      :project-ids="invoicedProjectIds"
      @update="invoicedProjectIds = []"
      @cancel="invoicedProjectIds = []"
    />
  </q-dialog>
  <q-dialog
    v-model="showDeliveryNoteDialog"
    :maximized="isMobile"
    persistent
    class="dialog-md"
  >
    <project-delivery-note-dialog
      v-if="showDeliveryNoteDialog"
      :model-value="projectItem"
      @cancel="showDeliveryNoteDialog = false"
    />
  </q-dialog>
  <q-dialog v-model="showChangeStatusDialog" persistent class="dialog-xs">
    <q-card>
      <q-card-section class="row items-center" style="flex-flow: row;">
        <q-avatar
          icon="mdi-alert"
          color="warning"
          text-color="white"
          size="md"
        />
        <span class="q-ml-sm">{{ i18n.t('common.confirmProjectStatusChange') }}</span>
      </q-card-section>
      <q-card-section>
        <q-select
          v-model="selectedStatus"
          :options="projectStatuses"
          option-value="value"
          option-label="label"
          emit-value
          map-options
          outlined
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat :label="i18n.t('common.cancel')" @click="showChangeStatusDialog = false" />
        <q-btn :label="i18n.t('common.confirm')" color="primary" @click="onConfirmStatusChange" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style scoped lang="scss">
@media (max-width: 820px) {
  .project-common-header {
    flex-shrink: 0;
  }

  // Shrink the tab bar so BUDGET/COST_REPORT/ALLOCATIONS all fit on one row
  // without horizontal scrolling on narrow phones.
  :deep(.q-tabs) {
    .q-tab {
      min-width: 0;
      padding: 0 .5rem;
    }

    .q-tab__label {
      font-size: .6875rem;
    }
  }

  // Budget tab only: flex column so tab panels fill remaining space
  .budget-content-scroll {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .budget-content-scroll :deep(.q-tab-panels) {
    flex: 1 !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }

  .budget-content-scroll :deep(.budget-tab-panel) {
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    min-height: 0 !important;
    padding: 0 !important;
  }

  .budget-extras {
    flex-shrink: 0;
  }

  .budget-embedded {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
  }

  .budget-embedded :deep(.dialog-card-flex) {
    max-height: none !important;
    flex: 1 !important;
    min-height: 0;
  }

  .budget-embedded :deep(.dialog-card-content-scroll) {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  // Allocations tab: same flex column treatment so Assign pins above the tab bar
  .budget-content-scroll :deep(.allocations-tab-panel) {
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    min-height: 0 !important;
    padding: 0 !important;
  }

  // TimelineDetail card fills the allocations tab panel; internal scroll handles services
  .budget-content-scroll :deep(.allocations-tab-panel .dialog-card-flex) {
    max-height: none !important;
    flex: 1 !important;
    min-height: 0;
  }

  .budget-content-scroll :deep(.tasks-tab-panel) {
    height: 100%;
  }
}
</style>
